import React from 'react'
import { useNavigate } from 'react-router-dom'
import { NextStepReact } from 'nextstepjs'
import { useAppTourNavigationAdapter } from './use-app-tour-navigation-adapter'
import { useSessionContext } from 'src/context/session-context'
import { coreService } from 'src/service/core-service'
import { localStorageService } from 'src/common/service/local-storage-service'
import { AnalyticsEvent } from 'src/service/analytics-events'
import { analyticsService } from 'src/service/analytics-service'
import { AppTourCard } from './app-tour-card'
import { useAppTourSteps } from './use-app-tour-steps'
import { isFeatureFlagged } from 'src/util/feature-flags'

const isAppTourFlagged = isFeatureFlagged('APP_TOUR')

export const AppTour = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const navigate = useNavigate()
  const { refreshMyData } = useSessionContext()
  const { tours, navWorkoutsStepIndex } = useAppTourSteps()

  if (isAppTourFlagged) {
    return <>{children}</>
  }

  return (
    <NextStepReact
      cardTransition={{
        ease: 'easeOut',
        duration: 0.4,
        stiffness: 100,
        damping: 10,
      }}
      shadowOpacity="0.7"
      overlayZIndex={1100}
      steps={tours}
      cardComponent={AppTourCard}
      navigationAdapter={useAppTourNavigationAdapter}
      onStart={() => analyticsService.track(AnalyticsEvent.APP_TOUR_STARTED)}
      onStepChange={(step) => {
        // The nav is `position: fixed`, so its rect is already scroll-invariant — but
        // nextstepjs's own position math assumes normal document flow and adds the current
        // scroll offset regardless, which put the tooltip off by however far the page had
        // scrolled for the previous (Suggested Workouts) step. Scrolling to the top before
        // nextstepjs computes this step's position sidesteps that mismatch entirely (GH-935).
        if (step === navWorkoutsStepIndex) {
          window.scrollTo({ top: 0, behavior: 'auto' })
        }
        localStorageService.setAppTourStep(step)
      }}
      onComplete={() => {
        void coreService.completeTour().then(() => {
          localStorageService.clearAppTourStep()
          void refreshMyData()
          navigate('/', { replace: true })
        })
        analyticsService.track(AnalyticsEvent.APP_TOUR_COMPLETED)
      }}
      onSkip={(step) => {
        void coreService.completeTour().then(() => {
          localStorageService.clearAppTourStep()
          void refreshMyData()
        })
        analyticsService.track(AnalyticsEvent.APP_TOUR_DISMISSED, { step: step + 1 })
      }}
    >
      {children}
    </NextStepReact>
  )
}
